import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row
} from 'reactstrap';
import { toast } from 'react-toastify';
import AppContext from '../../context/Context';
import apiBase from '../../apiConfig';
import useStoredTablePageSize from '../../hooks/useStoredTablePageSize';
import ActionConfirmModal from '../common/ActionConfirmModal';
import CollapsibleFilterSection from '../common/CollapsibleFilterSection';
import MedDataTable from '../common/MedDataTable';
import PageHeader from '../common/PageHeader';
import QuillEditor from '../common/QuillEditor';
import TablePageSizeSelect from '../common/TablePageSizeSelect';
import UzDatePicker from '../common/UzDatePicker';

const today = new Date().toISOString().slice(0, 10);

const doctorSections = [
  { key: 'cabinet', label: 'Qabul va natijalar', accent: 'primary', statLabel: 'bemor' },
  { key: 'partners', label: 'Hamkor shifokorlar', accent: 'info', statLabel: 'hamkor' }
];

const createInitialFilters = () => ({
  id: '',
  surname: '',
  name: '',
  lastname: '',
  from: today,
  to: today,
  filial_id: ''
});

const createInitialPartnerFilters = () => ({
  id: '',
  surname: '',
  name: '',
  lastname: '',
  region: '',
  work: '',
  profession: '',
  bid: '',
  exhausted_only: 0
});

const emptyDetail = {
  patient: null,
  services: []
};

const initialPartnerForm = {
  surname: '',
  name: '',
  lastname: '',
  sex: 1,
  phone: '',
  email: '',
  date_birth: '',
  work: '',
  profession: '',
  region: '',
  protsent: '',
  user_id: ''
};

const emptyPartnerDetail = {
  partner: null,
  blanks: [],
  blankless_referrals: [],
  summary: null
};

const initialBlankForm = {
  onebarcod: '',
  firstbarcod: '',
  lastbarcod: ''
};

const emptyPatientHistory = {
  patient: null,
  logs: [],
  payments: [],
  summary: null
};

const formatMoney = value => Number(value || 0).toLocaleString('ru-RU');

const buildServiceDrafts = services =>
  services.reduce((acc, service) => {
    acc[service.log_id] = {
      template_id: service.selected_template_id ? String(service.selected_template_id) : '',
      result_ready: service.result_ready ? 1 : 0,
      text: service.result_text || ''
    };
    return acc;
  }, {});

const createPartnerForm = partner => ({
  surname: partner?.surname || '',
  name: partner?.name || '',
  lastname: partner?.lastname || '',
  sex: Number(partner?.sex || 1),
  phone: partner?.phone || '',
  email: partner?.email || '',
  date_birth: partner?.date_birth || '',
  work: partner?.work || '',
  profession: partner?.profession || '',
  region: partner?.region || '',
  protsent: partner?.protsent ?? '',
  user_id: partner?.user_id ? String(partner.user_id) : ''
});

const analysisPreview = value => {
  const plain = String(value || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (plain.length <= 120) {
    return plain || '-';
  }

  return `${plain.slice(0, 120).trim()}...`;
};

const createAnalysisDrafts = items =>
  items.reduce((acc, item) => {
    acc[item.id] = {
      result: item.result || '',
      status: item.status_key || 'pending',
      template_id: item.template_id ? String(item.template_id) : '',
      method_id: item.method_id ? String(item.method_id) : '',
      norm_ids: Array.isArray(item.norm_ids) ? item.norm_ids.map(id => Number(id)) : []
    };
    return acc;
  }, {});

const Doctors = () => {
  const { user } = useContext(AppContext);
  const canManagePartners = user?.user_type === 'admin';
  const [activeSectionKey, setActiveSectionKey] = useState('cabinet');
  const [pageSize, setPageSize] = useStoredTablePageSize();
  const [filters, setFilters] = useState(createInitialFilters);
  const [appliedFilters, setAppliedFilters] = useState(createInitialFilters);
  const [patients, setPatients] = useState([]);
  const [filials, setFilials] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detail, setDetail] = useState(emptyDetail);
  const [serviceDrafts, setServiceDrafts] = useState({});
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [partnerFilters, setPartnerFilters] = useState(createInitialPartnerFilters);
  const [appliedPartnerFilters, setAppliedPartnerFilters] = useState(createInitialPartnerFilters);
  const [partners, setPartners] = useState([]);
  const [partnerRegions, setPartnerRegions] = useState([]);
  const [partnerUsers, setPartnerUsers] = useState([]);
  const [isPartnerLoading, setIsPartnerLoading] = useState(false);
  const [partnerModalOpen, setPartnerModalOpen] = useState(false);
  const [partnerDetailModalOpen, setPartnerDetailModalOpen] = useState(false);
  const [partnerForm, setPartnerForm] = useState(initialPartnerForm);
  const [editingPartnerId, setEditingPartnerId] = useState(null);
  const [partnerDetail, setPartnerDetail] = useState(emptyPartnerDetail);
  const [isPartnerDetailLoading, setIsPartnerDetailLoading] = useState(false);
  const [isPartnerSaving, setIsPartnerSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [blankModalOpen, setBlankModalOpen] = useState(false);
  const [blankForm, setBlankForm] = useState(initialBlankForm);
  const [blankEntryMode, setBlankEntryMode] = useState('single');
  const [blankSuggestionField, setBlankSuggestionField] = useState('');
  const [blankSuggestions, setBlankSuggestions] = useState([]);
  const [blankSuggestionsLoading, setBlankSuggestionsLoading] = useState(false);
  const [selectedBlankIds, setSelectedBlankIds] = useState([]);
  const [blankTransferPartnerId, setBlankTransferPartnerId] = useState('');
  const [isBlankSaving, setIsBlankSaving] = useState(false);
  const [patientHistoryModalOpen, setPatientHistoryModalOpen] = useState(false);
  const [patientHistory, setPatientHistory] = useState(emptyPatientHistory);
  const [isPatientHistoryLoading, setIsPatientHistoryLoading] = useState(false);
  const [analysisModalOpen, setAnalysisModalOpen] = useState(false);
  const [analysisPatient, setAnalysisPatient] = useState(null);
  const [analysisItems, setAnalysisItems] = useState([]);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisDrafts, setAnalysisDrafts] = useState({});
  const [analysisSavingId, setAnalysisSavingId] = useState(null);

  const activeSection = doctorSections.find(section => section.key === activeSectionKey) || doctorSections[0];

  const fetchFilials = async () => {
    const res = await axios.get(`${apiBase}/reference.php`, { params: { type: 'filials' } });
    if (res.data.success) {
      setFilials(res.data.items || []);
    }
  };

  const fetchPatients = async activeFilters => {
    setIsLoading(true);

    try {
      const params = {
        limit: 500,
        filial_id: activeFilters.filial_id || undefined,
        id_filter: activeFilters.id || undefined,
        surname: activeFilters.surname || undefined,
        name: activeFilters.name || undefined,
        lastname: activeFilters.lastname || undefined,
        from: activeFilters.from || undefined,
        to: activeFilters.to || undefined
      };

      const res = await axios.get(`${apiBase}/doctors.php`, { params });
      if (res.data.success) {
        setPatients(res.data.patients || []);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Shifokorlar bo'limi ma'lumotlari yuklanmadi");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPartners = async activeFilters => {
    setIsPartnerLoading(true);

    try {
      const params = {
        limit: 500,
        id_filter: activeFilters.id || undefined,
        surname: activeFilters.surname || undefined,
        name: activeFilters.name || undefined,
        lastname: activeFilters.lastname || undefined,
        region: activeFilters.region || undefined,
        work: activeFilters.work || undefined,
        profession: activeFilters.profession || undefined,
        bid: activeFilters.bid || undefined,
        exhausted_only: activeFilters.exhausted_only ? 1 : undefined
      };

      const res = await axios.get(`${apiBase}/partners.php`, { params });
      if (res.data.success) {
        setPartners(res.data.partners || []);
        setPartnerRegions(res.data.regions || []);
        setPartnerUsers(res.data.users || []);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Hamkor shifokorlar ma'lumotlari yuklanmadi");
    } finally {
      setIsPartnerLoading(false);
    }
  };

  useEffect(() => {
    fetchFilials().catch(error => {
      toast.error(error.response?.data?.message || 'Filiallar yuklanmadi');
    });
  }, []);

  useEffect(() => {
    fetchPatients(appliedFilters).catch(console.error);
  }, [appliedFilters]);

  useEffect(() => {
    fetchPartners(appliedPartnerFilters).catch(console.error);
  }, [appliedPartnerFilters]);

  useEffect(() => {
    if (!blankModalOpen || !blankSuggestionField) {
      setBlankSuggestions([]);
      setBlankSuggestionsLoading(false);
      return undefined;
    }

    const query = String(blankForm[blankSuggestionField] || '').trim();
    if (query.length === 0) {
      setBlankSuggestions([]);
      setBlankSuggestionsLoading(false);
      return undefined;
    }

    setBlankSuggestionsLoading(true);
    const timeout = setTimeout(() => {
      axios
        .get(`${apiBase}/partners.php`, { params: { blank_query: query } })
        .then(res => {
          if (res.data?.success) {
            setBlankSuggestions(res.data.items || []);
          } else {
            setBlankSuggestions([]);
          }
        })
        .catch(() => {
          setBlankSuggestions([]);
        })
        .finally(() => {
          setBlankSuggestionsLoading(false);
        });
    }, 180);

    return () => clearTimeout(timeout);
  }, [blankModalOpen, blankSuggestionField, blankForm]);

  const openPatient = async patient => {
    setSelectedPatientId(patient.id);
    setDetailModalOpen(true);
    setIsDetailLoading(true);
    setDetail(emptyDetail);
    setServiceDrafts({});

    try {
      const res = await axios.get(`${apiBase}/doctors.php`, { params: { id: patient.id } });
      if (!res.data?.success) {
        throw new Error('Bemor tafsilotlari yuklanmadi');
      }

      setDetail({
        patient: res.data.patient || null,
        services: res.data.services || []
      });
      setServiceDrafts(buildServiceDrafts(res.data.services || []));
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Bemor tafsilotlari yuklanmadi');
      setDetailModalOpen(false);
      setSelectedPatientId(null);
    } finally {
      setIsDetailLoading(false);
    }
  };

  const closePatientModal = () => {
    if (isSaving) {
      return;
    }

    setDetailModalOpen(false);
    setSelectedPatientId(null);
    setDetail(emptyDetail);
    setServiceDrafts({});
  };

  const handleFilterChange = (field, value) => {
    setFilters(current => ({
      ...current,
      [field]: value
    }));
  };

  const handleApplyFilters = event => {
    event.preventDefault();
    setAppliedFilters({ ...filters });
  };

  const handleResetFilters = () => {
    const nextFilters = createInitialFilters();
    setFilters(nextFilters);
    setAppliedFilters(nextFilters);
  };

  const updateServiceDraft = (logId, patch) => {
    setServiceDrafts(current => ({
      ...current,
      [logId]: {
        ...(current[logId] || {}),
        ...patch
      }
    }));
  };

  const handleTemplateChange = (service, templateIdValue) => {
    const selectedTemplate = (service.templates || []).find(template => String(template.id) === templateIdValue);
    updateServiceDraft(service.log_id, {
      template_id: templateIdValue,
      text: selectedTemplate ? selectedTemplate.text || '' : ''
    });
  };

  const handleSave = async () => {
    if (!detail.patient || detail.services.length === 0) {
      return;
    }

    setIsSaving(true);

    try {
      const payload = {
        patient_id: detail.patient.id,
        services: detail.services.map(service => {
          const draft = serviceDrafts[service.log_id] || {};
          return {
            log_id: service.log_id,
            template_id: Number(draft.template_id || 0),
            result_ready: draft.result_ready ? 1 : 0,
            text: draft.text || ''
          };
        })
      };

      const res = await axios.post(`${apiBase}/doctors.php`, payload);
      toast.success(res.data?.message || 'Doktor natijalari saqlandi');

      if (selectedPatientId) {
        await openPatient({ id: selectedPatientId });
      }
      fetchPatients(appliedFilters).catch(console.error);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Natijani saqlashda xatolik yuz berdi');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePartnerFilterChange = (field, value) => {
    setPartnerFilters(current => ({
      ...current,
      [field]: value
    }));
  };

  const handleApplyPartnerFilters = event => {
    event.preventDefault();
    setAppliedPartnerFilters({ ...partnerFilters });
  };

  const handleResetPartnerFilters = () => {
    const nextFilters = createInitialPartnerFilters();
    setPartnerFilters(nextFilters);
    setAppliedPartnerFilters(nextFilters);
  };

  const openPartnerCreate = () => {
    setEditingPartnerId(null);
    setPartnerForm(initialPartnerForm);
    setPartnerModalOpen(true);
  };

  const openPartnerEdit = partner => {
    setEditingPartnerId(partner.id);
    setPartnerForm(createPartnerForm(partner));
    setPartnerModalOpen(true);
  };

  const closePartnerFormModal = () => {
    if (isPartnerSaving) {
      return;
    }

    setPartnerModalOpen(false);
    setEditingPartnerId(null);
    setPartnerForm(initialPartnerForm);
  };

  const openPartnerDetail = async partnerId => {
    setPartnerDetailModalOpen(true);
    setIsPartnerDetailLoading(true);
    setPartnerDetail(emptyPartnerDetail);
    setSelectedBlankIds([]);
    setBlankTransferPartnerId('');

    try {
      const res = await axios.get(`${apiBase}/partners.php`, { params: { id: partnerId } });
      if (!res.data?.success) {
        throw new Error("Hamkor shifokor ma'lumotlari yuklanmadi");
      }

      setPartnerDetail({
        partner: res.data.partner || null,
        blanks: res.data.blanks || [],
        blankless_referrals: res.data.blankless_referrals || [],
        summary: res.data.summary || null
      });
      if ((res.data.partners || []).length > 0) {
        setPartnerUsers(current => (current.length > 0 ? current : res.data.users || []));
      }
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || "Hamkor shifokor ma'lumotlari yuklanmadi");
      setPartnerDetailModalOpen(false);
    } finally {
      setIsPartnerDetailLoading(false);
    }
  };

  const refreshPartnerDetail = async partnerId => {
    const res = await axios.get(`${apiBase}/partners.php`, { params: { id: partnerId } });
    if (res.data?.success) {
      setPartnerDetail({
        partner: res.data.partner || null,
        blanks: res.data.blanks || [],
        blankless_referrals: res.data.blankless_referrals || [],
        summary: res.data.summary || null
      });
      setSelectedBlankIds([]);
      return true;
    }

    return false;
  };

  const handlePartnerSubmit = async event => {
    event.preventDefault();
    setIsPartnerSaving(true);

    try {
      const payload = {
        ...partnerForm,
        sex: Number(partnerForm.sex || 0),
        protsent: Number(partnerForm.protsent || 0),
        user_id: Number(partnerForm.user_id || 0)
      };

      if (editingPartnerId) {
        const res = await axios.put(`${apiBase}/partners.php`, { ...payload, id: editingPartnerId });
        toast.success(res.data?.message || 'Hamkor shifokor yangilandi');
      } else {
        const res = await axios.post(`${apiBase}/partners.php`, payload);
        toast.success(res.data?.message || 'Hamkor shifokor yaratildi');
      }

      closePartnerFormModal();
      fetchPartners(appliedPartnerFilters).catch(console.error);
    } catch (error) {
      toast.error(error.response?.data?.message || "Hamkor shifokorni saqlashda xatolik yuz berdi");
    } finally {
      setIsPartnerSaving(false);
    }
  };

  const handleDeletePartner = async () => {
    if (!deleteTarget) {
      return;
    }

    try {
      const res = await axios.delete(`${apiBase}/partners.php`, { data: { id: deleteTarget.id } });
      toast.success(res.data?.message || "Hamkor shifokor o'chirildi");
      setDeleteTarget(null);
      fetchPartners(appliedPartnerFilters).catch(console.error);
    } catch (error) {
      toast.error(error.response?.data?.message || "Hamkor shifokorni o'chirishda xatolik yuz berdi");
    }
  };

  const closeBlankModal = () => {
    if (isBlankSaving) {
      return;
    }

    setBlankModalOpen(false);
    setBlankForm(initialBlankForm);
    setBlankEntryMode('single');
    setBlankSuggestionField('');
    setBlankSuggestions([]);
  };

  const handleToggleBlankSelection = blankId => {
    setSelectedBlankIds(current =>
      current.includes(blankId) ? current.filter(id => id !== blankId) : [...current, blankId]
    );
  };

  const handleToggleAllBlanks = checked => {
    if (!checked) {
      setSelectedBlankIds([]);
      return;
    }

    setSelectedBlankIds((partnerDetail.blanks || []).map(item => item.id));
  };

  const applyBlankSuggestion = suggestion => {
    if (!blankSuggestionField) {
      return;
    }

    const value = suggestion?.barcod || '';
    setBlankForm(current => ({
      ...current,
      [blankSuggestionField]: value
    }));
    setBlankSuggestions([]);
    setBlankSuggestionField('');
  };

  const handleBlankSubmit = async event => {
    event.preventDefault();
    if (!partnerDetail.partner) {
      return;
    }

    setIsBlankSaving(true);
    try {
      const payload = {
        entity: 'blank',
        partner_id: partnerDetail.partner.id,
        ...blankForm
      };
      const res = await axios.post(`${apiBase}/partners.php`, payload);
      toast.success(res.data?.message || 'Blanklar qo‘shildi');
      closeBlankModal();
      await refreshPartnerDetail(partnerDetail.partner.id);
      fetchPartners(appliedPartnerFilters).catch(console.error);
    } catch (error) {
      toast.error(error.response?.data?.message || "Blank qo'shishda xatolik yuz berdi");
    } finally {
      setIsBlankSaving(false);
    }
  };

  const handleBlankTransfer = async () => {
    if (!partnerDetail.partner || !blankTransferPartnerId || selectedBlankIds.length === 0) {
      return;
    }

    setIsBlankSaving(true);
    try {
      const res = await axios.put(`${apiBase}/partners.php`, {
        entity: 'blank',
        partner_id: Number(blankTransferPartnerId),
        blank_ids: selectedBlankIds
      });
      toast.success(res.data?.message || 'Blanklar ko‘chirildi');
      setBlankTransferPartnerId('');
      await refreshPartnerDetail(partnerDetail.partner.id);
      fetchPartners(appliedPartnerFilters).catch(console.error);
    } catch (error) {
      toast.error(error.response?.data?.message || "Blanklarni ko'chirishda xatolik yuz berdi");
    } finally {
      setIsBlankSaving(false);
    }
  };

  const handleBlankDelete = async () => {
    if (!partnerDetail.partner || selectedBlankIds.length === 0) {
      return;
    }

    setIsBlankSaving(true);
    try {
      const res = await axios.delete(`${apiBase}/partners.php`, {
        data: {
          entity: 'blank',
          blank_ids: selectedBlankIds
        }
      });
      toast.success(res.data?.message || "Blanklar o'chirildi");
      await refreshPartnerDetail(partnerDetail.partner.id);
      fetchPartners(appliedPartnerFilters).catch(console.error);
    } catch (error) {
      toast.error(error.response?.data?.message || "Blanklarni o'chirishda xatolik yuz berdi");
    } finally {
      setIsBlankSaving(false);
    }
  };

  const openPatientHistory = async patientId => {
    if (!patientId) {
      return;
    }

    setPatientHistoryModalOpen(true);
    setIsPatientHistoryLoading(true);
    setPatientHistory(emptyPatientHistory);

    try {
      const res = await axios.get(`${apiBase}/partners.php`, { params: { patient_id: patientId } });
      if (!res.data?.success) {
        throw new Error("Bemor tarixi yuklanmadi");
      }

      setPatientHistory({
        patient: res.data.patient || null,
        logs: res.data.logs || [],
        payments: res.data.payments || [],
        summary: res.data.summary || null
      });
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Bemor tarixi yuklanmadi');
      setPatientHistoryModalOpen(false);
    } finally {
      setIsPatientHistoryLoading(false);
    }
  };

  const openAnalysisDocumentation = async patient => {
    if (!patient?.id) {
      return;
    }

    setAnalysisPatient(patient);
    setAnalysisItems([]);
    setAnalysisModalOpen(true);
    setAnalysisLoading(true);

    try {
      const res = await axios.get(`${apiBase}/laboratory.php`, {
        params: {
          patient_id: patient.id,
          limit: 500
        }
      });

      if (res.data?.success) {
        const items = res.data.control || [];
        setAnalysisItems(items);
        setAnalysisDrafts(createAnalysisDrafts(items));
      } else {
        throw new Error("Analiz ma'lumotlari yuklanmadi");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || "Analiz ma'lumotlari yuklanmadi");
      setAnalysisModalOpen(false);
      setAnalysisPatient(null);
    } finally {
      setAnalysisLoading(false);
    }
  };

  const closeAnalysisModal = () => {
    setAnalysisModalOpen(false);
    setAnalysisPatient(null);
    setAnalysisItems([]);
    setAnalysisLoading(false);
    setAnalysisDrafts({});
    setAnalysisSavingId(null);
  };

  const updateAnalysisDraft = (id, patch) => {
    setAnalysisDrafts(current => ({
      ...current,
      [id]: {
        ...(current[id] || {}),
        ...patch
      }
    }));
  };

  const handleAnalysisTemplateChange = (item, templateId) => {
    const selectedTemplate = (item.templates || []).find(template => String(template.id) === String(templateId));
    updateAnalysisDraft(item.id, {
      template_id: templateId,
      result: selectedTemplate ? selectedTemplate.text || '' : ''
    });
  };

  const handleAnalysisMethodChange = (itemId, methodId) => {
    updateAnalysisDraft(itemId, {
      method_id: methodId,
      norm_ids: []
    });
  };

  const toggleAnalysisNorm = (itemId, normId) => {
    setAnalysisDrafts(current => {
      const currentDraft = current[itemId] || {};
      const normIds = Array.isArray(currentDraft.norm_ids) ? currentDraft.norm_ids : [];
      const numericNormId = Number(normId);

      return {
        ...current,
        [itemId]: {
          ...currentDraft,
          norm_ids: normIds.includes(numericNormId)
            ? normIds.filter(id => id !== numericNormId)
            : [...normIds, numericNormId]
        }
      };
    });
  };

  const saveAnalysisItem = async item => {
    const draft = analysisDrafts[item.id] || {};
    setAnalysisSavingId(item.id);

    try {
      const res = await axios.put(`${apiBase}/laboratory.php`, {
        entity: 'control',
        id: item.id,
        result: draft.result || '',
        status: draft.status || 'pending',
        template_id: Number(draft.template_id || 0),
        method_id: Number(draft.method_id || 0),
        norm_ids: draft.norm_ids || []
      });

      const updatedItem = res.data?.item;
      if (res.data?.success && updatedItem) {
        setAnalysisItems(current => current.map(entry => (entry.id === updatedItem.id ? updatedItem : entry)));
        setAnalysisDrafts(current => ({
          ...current,
          [updatedItem.id]: {
            result: updatedItem.result || '',
            status: updatedItem.status_key || 'pending',
            template_id: updatedItem.template_id ? String(updatedItem.template_id) : '',
            method_id: updatedItem.method_id ? String(updatedItem.method_id) : '',
            norm_ids: Array.isArray(updatedItem.norm_ids) ? updatedItem.norm_ids.map(id => Number(id)) : []
          }
        }));
        toast.success(res.data.message || 'Analiz saqlandi');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Analiz natijasini saqlashda xatolik yuz berdi');
    } finally {
      setAnalysisSavingId(null);
    }
  };

  const patientMetrics = {
    total: patients.length,
    debt: patients.filter(item => Number(item.debt) > 0).length,
    today: patients.filter(item => String(item.visit_date || '').slice(0, 10) === today).length,
    filial: appliedFilters.filial_id ? patients.length : filials.length
  };

  const partnerMetrics = {
    total: partners.length,
    exhausted: partners.filter(item => item.is_blank_exhausted).length,
    blanks: partners.reduce((sum, item) => sum + Number(item.blank_count || 0), 0),
    amount: partners.reduce((sum, item) => sum + Number(item.referral_amount || 0), 0)
  };

  const recommendedNextBarcode = (() => {
    const values = (partnerDetail.blanks || [])
      .map(item => Number(item.barcod))
      .filter(value => Number.isFinite(value) && value > 0);

    if (values.length === 0) {
      return '';
    }

    return String(Math.max(...values) + 1);
  })();

  const patientColumns = [
    { dataField: 'id', text: 'ID', sort: true, classes: 'align-middle font-weight-semi-bold' },
    { dataField: 'full_name', text: 'F.I.O', sort: true, classes: 'align-middle font-weight-bold' },
    { dataField: 'date_birth', text: "Tug'ilgan sana", sort: true, classes: 'align-middle', formatter: value => value || '-' },
    { dataField: 'filial_name', text: 'Filial', sort: true, classes: 'align-middle', formatter: value => value || '-' },
    {
      dataField: 'sex_label',
      text: 'Jins',
      sort: true,
      classes: 'align-middle',
      formatter: value => <span className="text-600">{value || '-'}</span>
    },
    { dataField: 'phone', text: 'Telefon', sort: true, classes: 'align-middle', formatter: value => value || '-' },
    { dataField: 'visit_date', text: 'Tashrif sanasi', sort: true, classes: 'align-middle', formatter: value => value || '-' },
    {
      dataField: 'debt',
      text: 'Qarz',
      sort: true,
      classes: 'align-middle',
      formatter: value => (
        <Badge color={Number(value) > 0 ? 'soft-danger' : 'soft-success'} pill>
          {formatMoney(value)}
        </Badge>
      )
    },
    {
      dataField: 'actions',
      text: 'Amallar',
      isDummyField: true,
      headerStyle: { width: '7rem' },
      classes: 'align-middle',
      formatter: (cell, patient) => (
        <div className="med-actions-row">
          <Button type="button" color="link" className="p-0 med-action-btn" onClick={() => openPatient(patient)}>
            <FontAwesomeIcon icon="pencil-alt" />
          </Button>
        </div>
      )
    }
  ];

  const partnerColumns = [
    { dataField: 'id', text: 'ID', sort: true, classes: 'align-middle font-weight-semi-bold' },
    { dataField: 'full_name', text: 'F.I.O', sort: true, classes: 'align-middle font-weight-bold' },
    { dataField: 'date_birth', text: "Tug'ilgan sana", sort: true, classes: 'align-middle', formatter: value => value || '-' },
    { dataField: 'sex_label', text: 'Jins', sort: true, classes: 'align-middle', formatter: value => value || '-' },
    { dataField: 'phone', text: 'Telefon', sort: true, classes: 'align-middle', formatter: value => value || '-' },
    { dataField: 'region', text: 'Hudud', sort: true, classes: 'align-middle', formatter: value => value || '-' },
    { dataField: 'work', text: 'Ish joyi', sort: true, classes: 'align-middle', formatter: value => value || '-' },
    { dataField: 'profession', text: 'Kasbi', sort: true, classes: 'align-middle', formatter: value => value || '-' },
    {
      dataField: 'protsent',
      text: 'Foiz',
      sort: true,
      classes: 'align-middle',
      formatter: value => `${Number(value || 0)}%`
    },
    { dataField: 'user_name', text: 'Foydalanuvchi', sort: true, classes: 'align-middle', formatter: value => value || '-' },
    {
      dataField: 'blank_count',
      text: 'Blanklar',
      sort: true,
      classes: 'align-middle',
      formatter: (value, row) => (
        <div>
          <Badge color={row.is_blank_exhausted ? 'soft-warning' : 'soft-info'} pill>
            {Number(value || 0)}
          </Badge>
          <div className="fs--2 text-500 mt-1">Ishlatilgan: {Number(row.used_blank_count || 0)}</div>
        </div>
      )
    },
    {
      dataField: 'referral_amount',
      text: "Yo'naltirish summasi",
      sort: true,
      classes: 'align-middle',
      formatter: value => formatMoney(value)
    },
    {
      dataField: 'actions',
      text: 'Amallar',
      isDummyField: true,
      headerStyle: { width: canManagePartners ? '9rem' : '4rem' },
      classes: 'align-middle',
      formatter: (cell, partner) => (
        <div className="med-actions-row">
          <Button type="button" color="link" className="p-0 med-action-btn" onClick={() => openPartnerDetail(partner.id)}>
            <FontAwesomeIcon icon="user-md" />
          </Button>
          {canManagePartners && (
            <>
              <Button type="button" color="link" className="p-0 med-action-btn" onClick={() => openPartnerEdit(partner)}>
                <FontAwesomeIcon icon="pencil-alt" />
              </Button>
              <Button
                type="button"
                color="link"
                className="p-0 text-danger med-action-btn med-action-btn-danger"
                onClick={() => setDeleteTarget({ id: partner.id, name: partner.full_name })}
              >
                <FontAwesomeIcon icon="trash-alt" />
              </Button>
            </>
          )}
        </div>
      )
    }
  ];

  const description =
    user?.user_type === 'doctor'
      ? "Qabul qilingan bemorlar natijalari va yo'naltiruvchi shifokorlar ma'lumoti shu sahifaga yig'ilgan."
      : "Eski doctor kabineti va partners bo'limidagi ma'lumotlar bitta sahifada boshqariladi.";

  const sectionCount = activeSectionKey === 'cabinet' ? patients.length : partners.length;

  return (
    <>
      <PageHeader title="Shifokorlar" description={description} className="mb-3" />

      <Card className="border-0 shadow-sm med-dashboard-card med-patient-filter-card mb-3">
        <CardBody className="med-dashboard-card-body">
          <CollapsibleFilterSection
            title="Shifokor bo'limlari"
            summary={`${activeSection.label} bo'limi tanlangan`}
            actions={
              <Badge color={`soft-${activeSection.accent}`} pill>
                {activeSection.label}: {sectionCount}
              </Badge>
            }
            storageKey="doctors-sections-collapse"
          >
            <div className="med-patient-filter-grid">
              {doctorSections.map(section => (
                <button
                  key={section.key}
                  type="button"
                  className={`med-patient-filter-item med-patient-filter-item-${section.accent}${
                    activeSectionKey === section.key ? ' is-active' : ''
                  }`}
                  onClick={() => setActiveSectionKey(section.key)}
                >
                  <span className="med-patient-filter-label">{section.label}</span>
                  <span className="med-patient-filter-meta">
                    <strong>{section.key === 'cabinet' ? patients.length : partners.length}</strong>
                    <small>{section.statLabel}</small>
                  </span>
                </button>
              ))}
            </div>
          </CollapsibleFilterSection>
        </CardBody>
      </Card>

      {activeSectionKey === 'cabinet' ? (
        <>
          <Row className="mb-3">
            <Col md={6} xl={3} className="mb-3">
              <Card className="border-0 shadow-sm med-dashboard-card h-100">
                <CardBody className="med-dashboard-card-body">
                  <small className="text-500 text-uppercase font-weight-bold">Bemorlar</small>
                  <div className="h3 mb-0 mt-2">{patientMetrics.total}</div>
                </CardBody>
              </Card>
            </Col>
            <Col md={6} xl={3} className="mb-3">
              <Card className="border-0 shadow-sm med-dashboard-card h-100">
                <CardBody className="med-dashboard-card-body">
                  <small className="text-500 text-uppercase font-weight-bold">Bugungi navbat</small>
                  <div className="h3 mb-0 mt-2 text-primary">{patientMetrics.today}</div>
                </CardBody>
              </Card>
            </Col>
            <Col md={6} xl={3} className="mb-3">
              <Card className="border-0 shadow-sm med-dashboard-card h-100">
                <CardBody className="med-dashboard-card-body">
                  <small className="text-500 text-uppercase font-weight-bold">Qarzdorlar</small>
                  <div className="h3 mb-0 mt-2 text-danger">{patientMetrics.debt}</div>
                </CardBody>
              </Card>
            </Col>
            <Col md={6} xl={3} className="mb-3">
              <Card className="border-0 shadow-sm med-dashboard-card h-100">
                <CardBody className="med-dashboard-card-body">
                  <small className="text-500 text-uppercase font-weight-bold">Filial ko'lami</small>
                  <div className="h3 mb-0 mt-2 text-info">{patientMetrics.filial}</div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Card className="border-0 shadow-sm med-dashboard-card mb-3">
            <CardBody className="med-dashboard-card-body">
              <CollapsibleFilterSection
                title="Shifokor filtrlari"
                summary={`${appliedFilters.from || today} - ${appliedFilters.to || today}${
                  appliedFilters.filial_id ? ' | Filial tanlangan' : ''
                }`}
                storageKey="doctors-filters-collapse"
              >
                <form onSubmit={handleApplyFilters}>
                  <Row className="align-items-end">
                    <Col md={2} className="mb-3">
                      <label className="med-filter-label">ID</label>
                      <Input
                        value={filters.id}
                        onChange={event => handleFilterChange('id', event.target.value)}
                        className="bg-200 border-0"
                        placeholder="ID"
                      />
                    </Col>
                    <Col md={2} className="mb-3">
                      <label className="med-filter-label">Familiya</label>
                      <Input
                        value={filters.surname}
                        onChange={event => handleFilterChange('surname', event.target.value)}
                        className="bg-200 border-0"
                        placeholder="Familiya"
                      />
                    </Col>
                    <Col md={2} className="mb-3">
                      <label className="med-filter-label">Ism</label>
                      <Input
                        value={filters.name}
                        onChange={event => handleFilterChange('name', event.target.value)}
                        className="bg-200 border-0"
                        placeholder="Ism"
                      />
                    </Col>
                    <Col md={2} className="mb-3">
                      <label className="med-filter-label">Otasining ismi</label>
                      <Input
                        value={filters.lastname}
                        onChange={event => handleFilterChange('lastname', event.target.value)}
                        className="bg-200 border-0"
                        placeholder="Otasining ismi"
                      />
                    </Col>
                    <Col md={2} className="mb-3">
                      <label className="med-filter-label">Dan</label>
                      <UzDatePicker
                        value={filters.from}
                        onChange={value => handleFilterChange('from', value)}
                        className="form-control bg-200 border-0"
                      />
                    </Col>
                    <Col md={2} className="mb-3">
                      <label className="med-filter-label">Gacha</label>
                      <UzDatePicker
                        value={filters.to}
                        onChange={value => handleFilterChange('to', value)}
                        className="form-control bg-200 border-0"
                      />
                    </Col>
                    <Col md={3} className="mb-3">
                      <label className="med-filter-label">Filial</label>
                      <Input
                        type="select"
                        value={filters.filial_id}
                        onChange={event => handleFilterChange('filial_id', event.target.value)}
                        className="bg-200 border-0"
                      >
                        <option value="">Barcha filiallar</option>
                        {filials.map(item => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col md="auto" className="mb-3">
                      <Button type="submit" color="primary" className="med-outline-add-btn">
                        <FontAwesomeIcon icon="search" className="mr-2" />
                        Qidirish
                      </Button>
                    </Col>
                    <Col md="auto" className="mb-3">
                      <Button type="button" color="light" className="med-outline-add-btn" onClick={handleResetFilters}>
                        Tozalash
                      </Button>
                    </Col>
                  </Row>
                </form>
              </CollapsibleFilterSection>
            </CardBody>
          </Card>

          <Card className="border-0 shadow-sm med-dashboard-card">
            <CardBody className="med-dashboard-card-body">
              <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
                <div>
                  <h5 className="mb-1">Qabul qilingan bemorlar</h5>
                  <small className="text-500">Ro'yxat eski `doctor&op=mgmnt` sahifasidagi filtr mantig'iga mos ishlaydi.</small>
                </div>
                <TablePageSizeSelect value={pageSize} onChange={setPageSize} className="med-table-page-size mt-3 mt-md-0" />
              </div>

              <MedDataTable
                data={patients}
                columns={patientColumns}
                pageSize={pageSize}
                paginationResetKey={`${patients.length}-${appliedFilters.id}-${appliedFilters.surname}-${appliedFilters.name}-${appliedFilters.lastname}-${appliedFilters.from}-${appliedFilters.to}-${appliedFilters.filial_id}`}
                rowClasses={row => (Number(row.debt) > 0 ? 'med-doctor-row-has-debt' : '')}
                noDataIndication={
                  <div className="text-center py-5 text-500">
                    {isLoading ? 'Yuklanmoqda...' : 'Tanlangan filterlar bo‘yicha bemor topilmadi.'}
                  </div>
                }
              />
            </CardBody>
          </Card>
        </>
      ) : (
        <>
          <Row className="mb-3">
            <Col md={6} xl={3} className="mb-3">
              <Card className="border-0 shadow-sm med-dashboard-card h-100">
                <CardBody className="med-dashboard-card-body">
                  <small className="text-500 text-uppercase font-weight-bold">Hamkorlar</small>
                  <div className="h3 mb-0 mt-2">{partnerMetrics.total}</div>
                </CardBody>
              </Card>
            </Col>
            <Col md={6} xl={3} className="mb-3">
              <Card className="border-0 shadow-sm med-dashboard-card h-100">
                <CardBody className="med-dashboard-card-body">
                  <small className="text-500 text-uppercase font-weight-bold">Blanki tugaganlar</small>
                  <div className="h3 mb-0 mt-2 text-warning">{partnerMetrics.exhausted}</div>
                </CardBody>
              </Card>
            </Col>
            <Col md={6} xl={3} className="mb-3">
              <Card className="border-0 shadow-sm med-dashboard-card h-100">
                <CardBody className="med-dashboard-card-body">
                  <small className="text-500 text-uppercase font-weight-bold">Jami blanklar</small>
                  <div className="h3 mb-0 mt-2 text-info">{partnerMetrics.blanks}</div>
                </CardBody>
              </Card>
            </Col>
            <Col md={6} xl={3} className="mb-3">
              <Card className="border-0 shadow-sm med-dashboard-card h-100">
                <CardBody className="med-dashboard-card-body">
                  <small className="text-500 text-uppercase font-weight-bold">Yo'naltirish summasi</small>
                  <div className="h3 mb-0 mt-2 text-success">{formatMoney(partnerMetrics.amount)}</div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Card className="border-0 shadow-sm med-dashboard-card mb-3">
            <CardBody className="med-dashboard-card-body">
              <CollapsibleFilterSection
                title="Hamkor shifokor filtrlari"
                summary={appliedPartnerFilters.exhausted_only ? "Blanki tugaganlar filtri yoqilgan" : 'Barcha hamkor shifokorlar'}
                storageKey="doctor-partners-filters-collapse"
              >
                <form onSubmit={handleApplyPartnerFilters}>
                  <Row className="align-items-end">
                    <Col md={2} className="mb-3">
                      <label className="med-filter-label">ID</label>
                      <Input
                        value={partnerFilters.id}
                        onChange={event => handlePartnerFilterChange('id', event.target.value)}
                        className="bg-200 border-0"
                        placeholder="ID"
                      />
                    </Col>
                    <Col md={2} className="mb-3">
                      <label className="med-filter-label">Familiya</label>
                      <Input
                        value={partnerFilters.surname}
                        onChange={event => handlePartnerFilterChange('surname', event.target.value)}
                        className="bg-200 border-0"
                        placeholder="Familiya"
                      />
                    </Col>
                    <Col md={2} className="mb-3">
                      <label className="med-filter-label">Ism</label>
                      <Input
                        value={partnerFilters.name}
                        onChange={event => handlePartnerFilterChange('name', event.target.value)}
                        className="bg-200 border-0"
                        placeholder="Ism"
                      />
                    </Col>
                    <Col md={2} className="mb-3">
                      <label className="med-filter-label">Otasining ismi</label>
                      <Input
                        value={partnerFilters.lastname}
                        onChange={event => handlePartnerFilterChange('lastname', event.target.value)}
                        className="bg-200 border-0"
                        placeholder="Otasining ismi"
                      />
                    </Col>
                    <Col md={2} className="mb-3">
                      <label className="med-filter-label">Hudud</label>
                      <Input
                        value={partnerFilters.region}
                        onChange={event => handlePartnerFilterChange('region', event.target.value)}
                        className="bg-200 border-0"
                        placeholder="Hudud"
                        list="doctor-partner-region-filter-options"
                      />
                    </Col>
                    <Col md={2} className="mb-3">
                      <label className="med-filter-label">Blank ID</label>
                      <Input
                        value={partnerFilters.bid}
                        onChange={event => handlePartnerFilterChange('bid', event.target.value)}
                        className="bg-200 border-0"
                        placeholder="Barcode"
                      />
                    </Col>
                    <Col md={3} className="mb-3">
                      <label className="med-filter-label">Ish joyi</label>
                      <Input
                        value={partnerFilters.work}
                        onChange={event => handlePartnerFilterChange('work', event.target.value)}
                        className="bg-200 border-0"
                        placeholder="Ish joyi"
                      />
                    </Col>
                    <Col md={3} className="mb-3">
                      <label className="med-filter-label">Kasbi</label>
                      <Input
                        value={partnerFilters.profession}
                        onChange={event => handlePartnerFilterChange('profession', event.target.value)}
                        className="bg-200 border-0"
                        placeholder="Kasbi"
                      />
                    </Col>
                    <Col md={3} className="mb-3">
                      <div className="d-flex align-items-center h-100 pt-md-4">
                        <Input
                          type="checkbox"
                          checked={Boolean(partnerFilters.exhausted_only)}
                          onChange={event => handlePartnerFilterChange('exhausted_only', event.target.checked ? 1 : 0)}
                          className="mr-2"
                        />
                        <span className="text-700">Blanki tugaganlarni ko'rsatish</span>
                      </div>
                    </Col>
                    <Col md="auto" className="mb-3">
                      <Button type="submit" color="primary" className="med-outline-add-btn">
                        <FontAwesomeIcon icon="search" className="mr-2" />
                        Qidirish
                      </Button>
                    </Col>
                    <Col md="auto" className="mb-3">
                      <Button type="button" color="light" className="med-outline-add-btn" onClick={handleResetPartnerFilters}>
                        Tozalash
                      </Button>
                    </Col>
                  </Row>
                </form>
                <datalist id="doctor-partner-region-filter-options">
                  {partnerRegions.map(region => (
                    <option key={region} value={region} />
                  ))}
                </datalist>
              </CollapsibleFilterSection>
            </CardBody>
          </Card>

          <Card className="border-0 shadow-sm med-dashboard-card">
            <CardBody className="med-dashboard-card-body">
              <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
                <div>
                  <h5 className="mb-1">Hamkor shifokorlar ro'yxati</h5>
                  <small className="text-500">Eski `partners&op=mgmnt` sahifasidagi ma'lumotlar, foiz va blank statistikasi bilan birga ko'rsatiladi.</small>
                </div>
                <div className="d-flex flex-wrap align-items-center mt-3 mt-md-0">
                  <TablePageSizeSelect value={pageSize} onChange={setPageSize} className="med-table-page-size" />
                  {canManagePartners && (
                    <Button type="button" color="outline-primary" className="med-outline-add-btn ml-3" onClick={openPartnerCreate}>
                      <FontAwesomeIcon icon="plus" className="mr-2" />
                      Yangi hamkor shifokor
                    </Button>
                  )}
                </div>
              </div>

              <MedDataTable
                data={partners}
                columns={partnerColumns}
                pageSize={pageSize}
                paginationResetKey={`${partners.length}-${appliedPartnerFilters.id}-${appliedPartnerFilters.surname}-${appliedPartnerFilters.name}-${appliedPartnerFilters.lastname}-${appliedPartnerFilters.region}-${appliedPartnerFilters.work}-${appliedPartnerFilters.profession}-${appliedPartnerFilters.bid}-${appliedPartnerFilters.exhausted_only}`}
                noDataIndication={
                  <div className="text-center py-5 text-500">
                    {isPartnerLoading ? 'Yuklanmoqda...' : "Tanlangan filterlar bo'yicha hamkor shifokor topilmadi."}
                  </div>
                }
              />
            </CardBody>
          </Card>
        </>
      )}

      <Modal isOpen={detailModalOpen} toggle={closePatientModal} size="xl" className="med-doctor-modal" centered>
        <ModalHeader toggle={closePatientModal}>
          {detail.patient ? `${detail.patient.full_name} bo'yicha natijalar` : 'Bemor tafsilotlari'}
        </ModalHeader>
        <ModalBody className="med-doctor-modal-body">
          {isDetailLoading ? (
            <div className="text-center py-5 text-500">Bemor ma'lumotlari yuklanmoqda...</div>
          ) : !detail.patient ? (
            <div className="text-center py-5 text-500">Bemor ma'lumotlari topilmadi.</div>
          ) : (
            <>
              <Row className="mb-3">
                <Col md={6} xl={3} className="mb-3">
                  <div className="med-doctor-summary-card">
                    <small>ID</small>
                    <strong>{detail.patient.id}</strong>
                    <span>{detail.patient.filial_name || 'Filial ko‘rsatilmagan'}</span>
                  </div>
                </Col>
                <Col md={6} xl={3} className="mb-3">
                  <div className="med-doctor-summary-card">
                    <small>Tug'ilgan sana</small>
                    <strong>{detail.patient.date_birth || '-'}</strong>
                    <span>{detail.patient.sex_label || '-'}</span>
                  </div>
                </Col>
                <Col md={6} xl={3} className="mb-3">
                  <div className="med-doctor-summary-card">
                    <small>Telefon</small>
                    <strong>{detail.patient.phone || '-'}</strong>
                    <span>Tashrif: {detail.patient.visit_date || '-'}</span>
                  </div>
                </Col>
                <Col md={6} xl={3} className="mb-3">
                  <div className="med-doctor-summary-card">
                    <small>Qarz holati</small>
                    <strong>{formatMoney(detail.patient.debt)}</strong>
                    <span>{Number(detail.patient.debt) > 0 ? 'Qarz mavjud' : "To'lov yopilgan"}</span>
                  </div>
                </Col>
              </Row>

              {detail.services.length === 0 ? (
                <div className="text-center py-5 text-500">Bu bemor uchun doctor xizmatlari topilmadi.</div>
              ) : (
                <div className="med-doctor-service-list">
                  {detail.services.map(service => {
                    const draft = serviceDrafts[service.log_id] || { template_id: '', result_ready: 0, text: '' };
                    const selectedTemplate = (service.templates || []).find(
                      template => String(template.id) === String(draft.template_id || '')
                    );

                    return (
                      <Card key={service.log_id} className="border-0 shadow-sm med-doctor-service-card">
                        <CardBody className="med-doctor-service-card-body">
                          <div className="d-flex flex-wrap align-items-start justify-content-between mb-3">
                            <div>
                              <h6 className="mb-1">{service.category_name || "Shifokor bo'limi"}</h6>
                              <div className="text-500 fs--1">
                                {service.subcategory_name || "Xizmat nomi ko'rsatilmagan"} | {service.date || '-'}
                              </div>
                            </div>
                            <Badge color={draft.result_ready ? 'soft-success' : 'soft-warning'} pill>
                              {draft.result_ready ? 'Natija tayyor' : 'Natija kutilmoqda'}
                            </Badge>
                          </div>

                          <Row>
                            <Col lg={4} className="mb-3">
                              <label className="med-filter-label">Shablon</label>
                              <Input
                                type="select"
                                value={draft.template_id}
                                onChange={event => handleTemplateChange(service, event.target.value)}
                                className="bg-200 border-0"
                              >
                                <option value="">Shablon tanlang</option>
                                {(service.templates || []).map(template => (
                                  <option key={template.id} value={template.id}>
                                    {template.name}
                                  </option>
                                ))}
                              </Input>
                              <div className="text-500 fs--2 mt-2">
                                {selectedTemplate ? "Tanlangan shablon matni editorga yuklanadi." : "Bu xizmat uchun shablon biriktirilmagan."}
                              </div>
                            </Col>
                            <Col lg={3} className="mb-3">
                              <label className="med-filter-label">Natija holati</label>
                              <div className="med-doctor-status-toggle">
                                <button
                                  type="button"
                                  className={draft.result_ready ? '' : 'is-active'}
                                  onClick={() => updateServiceDraft(service.log_id, { result_ready: 0 })}
                                >
                                  Kutilmoqda
                                </button>
                                <button
                                  type="button"
                                  className={draft.result_ready ? 'is-active' : ''}
                                  onClick={() => updateServiceDraft(service.log_id, { result_ready: 1 })}
                                >
                                  Tayyor
                                </button>
                              </div>
                            </Col>
                            <Col lg={5} className="mb-3">
                              <label className="med-filter-label">Saqlangan vaqt</label>
                              <div className="med-doctor-service-meta">
                                <span>{service.saved_at || 'Hali saqlanmagan'}</span>
                                <small>Log ID: {service.log_id}</small>
                              </div>
                            </Col>
                          </Row>

                          <label className="med-filter-label">Natija matni</label>
                          <QuillEditor
                            value={draft.text || ''}
                            onChange={value => updateServiceDraft(service.log_id, { text: value })}
                            className="med-doctor-editor"
                          />
                        </CardBody>
                      </Card>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button type="button" color="light" onClick={closePatientModal} disabled={isSaving}>
            Yopish
          </Button>
          <Button type="button" color="primary" onClick={handleSave} disabled={isSaving || isDetailLoading || !detail.patient}>
            <FontAwesomeIcon icon="save" className="mr-2" />
            {isSaving ? 'Saqlanmoqda...' : 'Natijani saqlash'}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={partnerModalOpen} toggle={closePartnerFormModal} size="lg" centered>
        <ModalHeader toggle={closePartnerFormModal}>
          {editingPartnerId ? 'Hamkor shifokorni tahrirlash' : 'Yangi hamkor shifokor'}
        </ModalHeader>
        <form onSubmit={handlePartnerSubmit}>
          <ModalBody>
            <Row>
              <Col md={4} className="mb-3">
                <label className="med-filter-label">Familiya</label>
                <Input
                  value={partnerForm.surname}
                  onChange={event => {
                    const value = event.target.value;
                    setPartnerForm(current => ({ ...current, surname: value }));
                  }}
                  className="bg-200 border-0"
                />
              </Col>
              <Col md={4} className="mb-3">
                <label className="med-filter-label">Ism</label>
                <Input
                  value={partnerForm.name}
                  onChange={event => {
                    const value = event.target.value;
                    setPartnerForm(current => ({ ...current, name: value }));
                  }}
                  className="bg-200 border-0"
                  required
                />
              </Col>
              <Col md={4} className="mb-3">
                <label className="med-filter-label">Otasining ismi</label>
                <Input
                  value={partnerForm.lastname}
                  onChange={event => {
                    const value = event.target.value;
                    setPartnerForm(current => ({ ...current, lastname: value }));
                  }}
                  className="bg-200 border-0"
                />
              </Col>
              <Col md={3} className="mb-3">
                <label className="med-filter-label">Jins</label>
                <Input
                  type="select"
                  value={partnerForm.sex}
                  onChange={event => {
                    const value = Number(event.target.value);
                    setPartnerForm(current => ({ ...current, sex: value }));
                  }}
                  className="bg-200 border-0"
                >
                  <option value={1}>Erkak</option>
                  <option value={2}>Ayol</option>
                </Input>
              </Col>
              <Col md={3} className="mb-3">
                <label className="med-filter-label">Tug'ilgan sana</label>
                <UzDatePicker
                  value={partnerForm.date_birth}
                  onChange={value => setPartnerForm(current => ({ ...current, date_birth: value }))}
                  className="form-control bg-200 border-0"
                />
              </Col>
              <Col md={3} className="mb-3">
                <label className="med-filter-label">Telefon</label>
                <Input
                  value={partnerForm.phone}
                  onChange={event => {
                    const value = event.target.value;
                    setPartnerForm(current => ({ ...current, phone: value }));
                  }}
                  className="bg-200 border-0"
                />
              </Col>
              <Col md={3} className="mb-3">
                <label className="med-filter-label">E-mail</label>
                <Input
                  type="email"
                  value={partnerForm.email}
                  onChange={event => {
                    const value = event.target.value;
                    setPartnerForm(current => ({ ...current, email: value }));
                  }}
                  className="bg-200 border-0"
                />
              </Col>
              <Col md={4} className="mb-3">
                <label className="med-filter-label">Hudud</label>
                <Input
                  value={partnerForm.region}
                  onChange={event => {
                    const value = event.target.value;
                    setPartnerForm(current => ({ ...current, region: value }));
                  }}
                  className="bg-200 border-0"
                  list="doctor-partner-region-options"
                  required
                />
              </Col>
              <Col md={4} className="mb-3">
                <label className="med-filter-label">Ish joyi</label>
                <Input
                  value={partnerForm.work}
                  onChange={event => {
                    const value = event.target.value;
                    setPartnerForm(current => ({ ...current, work: value }));
                  }}
                  className="bg-200 border-0"
                />
              </Col>
              <Col md={4} className="mb-3">
                <label className="med-filter-label">Kasbi</label>
                <Input
                  value={partnerForm.profession}
                  onChange={event => {
                    const value = event.target.value;
                    setPartnerForm(current => ({ ...current, profession: value }));
                  }}
                  className="bg-200 border-0"
                />
              </Col>
              <Col md={4} className="mb-3">
                <label className="med-filter-label">Foiz</label>
                <Input
                  type="number"
                  value={partnerForm.protsent}
                  onChange={event => {
                    const value = event.target.value;
                    setPartnerForm(current => ({ ...current, protsent: value }));
                  }}
                  className="bg-200 border-0"
                  min="0"
                />
              </Col>
              <Col md={8} className="mb-3">
                <label className="med-filter-label">Foydalanuvchi</label>
                <Input
                  type="select"
                  value={partnerForm.user_id}
                  onChange={event => {
                    const value = event.target.value;
                    setPartnerForm(current => ({ ...current, user_id: value }));
                  }}
                  className="bg-200 border-0"
                >
                  <option value="">Biriktirilmagan</option>
                  {partnerUsers.map(option => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </Input>
              </Col>
            </Row>
            <datalist id="doctor-partner-region-options">
              {partnerRegions.map(region => (
                <option key={region} value={region} />
              ))}
            </datalist>
          </ModalBody>
          <ModalFooter>
            <Button type="button" color="light" onClick={closePartnerFormModal} disabled={isPartnerSaving}>
              Bekor qilish
            </Button>
            <Button type="submit" color="primary" disabled={isPartnerSaving}>
              <FontAwesomeIcon icon="save" className="mr-2" />
              {isPartnerSaving ? 'Saqlanmoqda...' : 'Saqlash'}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      <Modal isOpen={partnerDetailModalOpen} toggle={() => setPartnerDetailModalOpen(false)} size="xl" centered>
        <ModalHeader toggle={() => setPartnerDetailModalOpen(false)}>
          {partnerDetail.partner ? `${partnerDetail.partner.full_name} ma'lumotlari` : "Hamkor shifokor tafsilotlari"}
        </ModalHeader>
        <ModalBody>
          {isPartnerDetailLoading ? (
            <div className="text-center py-5 text-500">Hamkor shifokor ma'lumotlari yuklanmoqda...</div>
          ) : !partnerDetail.partner ? (
            <div className="text-center py-5 text-500">Ma'lumot topilmadi.</div>
          ) : (
            <>
              <Row className="mb-3">
                <Col md={6} xl={3} className="mb-3">
                  <div className="med-doctor-summary-card">
                    <small>ID</small>
                    <strong>{partnerDetail.partner.id}</strong>
                    <span>{partnerDetail.partner.sex_label}</span>
                  </div>
                </Col>
                <Col md={6} xl={3} className="mb-3">
                  <div className="med-doctor-summary-card">
                    <small>Telefon / Email</small>
                    <strong>{partnerDetail.partner.phone || '-'}</strong>
                    <span>{partnerDetail.partner.email || '-'}</span>
                  </div>
                </Col>
                <Col md={6} xl={3} className="mb-3">
                  <div className="med-doctor-summary-card">
                    <small>Hudud / Kasbi</small>
                    <strong>{partnerDetail.partner.region || '-'}</strong>
                    <span>{partnerDetail.partner.profession || '-'}</span>
                  </div>
                </Col>
                <Col md={6} xl={3} className="mb-3">
                  <div className="med-doctor-summary-card">
                    <small>Foiz / Foydalanuvchi</small>
                    <strong>{Number(partnerDetail.partner.protsent || 0)}%</strong>
                    <span>{partnerDetail.partner.user_name || 'Biriktirilmagan'}</span>
                  </div>
                </Col>
              </Row>

              <Row className="mb-4">
                <Col md={6} xl={3} className="mb-3">
                  <div className="med-doctor-summary-card">
                    <small>Jami blanklar</small>
                    <strong>{partnerDetail.summary?.blank_count || 0}</strong>
                    <span>Ishlatilgan: {partnerDetail.summary?.used_blank_count || 0}</span>
                  </div>
                </Col>
                <Col md={6} xl={3} className="mb-3">
                  <div className="med-doctor-summary-card">
                    <small>Blanksiz yo'naltirish</small>
                    <strong>{partnerDetail.summary?.blankless_referral_count || 0}</strong>
                    <span>Qatorlar soni</span>
                  </div>
                </Col>
                <Col md={6} xl={3} className="mb-3">
                  <div className="med-doctor-summary-card">
                    <small>Jami yo'naltirish</small>
                    <strong>{partnerDetail.summary?.referral_count || 0}</strong>
                    <span>Tasdiqlangan tashriflar</span>
                  </div>
                </Col>
                <Col md={6} xl={3} className="mb-3">
                  <div className="med-doctor-summary-card">
                    <small>Jami summa</small>
                    <strong>{formatMoney(partnerDetail.summary?.referral_amount || 0)}</strong>
                    <span>Yo'naltirishlar bo'yicha</span>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col xl={7} className="mb-4">
                  <Card className="border-0 shadow-sm med-dashboard-card h-100">
                    <CardBody className="med-dashboard-card-body">
                      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                        <div className="mb-2 mb-md-0">
                          <h5 className="mb-0">Biriktirilgan blanklar</h5>
                          <small className="text-500">Eski `blank` sahifasidagi boshqaruv shu yerga yig'ilgan.</small>
                        </div>
                        <div className="d-flex flex-wrap align-items-center">
                          <Badge color="soft-info" pill className="mr-2">
                            {partnerDetail.blanks.length}
                          </Badge>
                          {canManagePartners && (
                            <Button
                              type="button"
                              color="outline-primary"
                              size="sm"
                              className="med-outline-add-btn"
                              onClick={() => {
                                setBlankEntryMode('single');
                                setBlankForm(initialBlankForm);
                                setBlankModalOpen(true);
                              }}
                            >
                              <FontAwesomeIcon icon="plus" className="mr-2" />
                              Blank qo'shish
                            </Button>
                          )}
                        </div>
                      </div>
                      {canManagePartners && partnerDetail.blanks.length > 0 && (
                        <div className="med-partner-blank-toolbar mb-3">
                          <label className="d-flex align-items-center mb-0 mr-3">
                            <Input
                              type="checkbox"
                              className="mr-2"
                              checked={selectedBlankIds.length > 0 && selectedBlankIds.length === partnerDetail.blanks.length}
                              onChange={event => handleToggleAllBlanks(event.target.checked)}
                            />
                            <span>Hammasini tanlash</span>
                          </label>
                          <Input
                            type="select"
                            value={blankTransferPartnerId}
                            onChange={event => setBlankTransferPartnerId(event.target.value)}
                            className="bg-200 border-0 med-partner-transfer-select"
                          >
                            <option value="">Boshqa hamkorni tanlang</option>
                            {partners
                              .filter(item => item.id !== partnerDetail.partner.id)
                              .map(item => (
                                <option key={item.id} value={item.id}>
                                  {item.full_name}
                                </option>
                              ))}
                          </Input>
                          <Button
                            type="button"
                            color="light"
                            size="sm"
                            onClick={handleBlankTransfer}
                            disabled={!blankTransferPartnerId || selectedBlankIds.length === 0 || isBlankSaving}
                          >
                            <FontAwesomeIcon icon="exchange-alt" className="mr-2" />
                            O'tkazish
                          </Button>
                          <Button
                            type="button"
                            color="danger"
                            size="sm"
                            onClick={handleBlankDelete}
                            disabled={selectedBlankIds.length === 0 || isBlankSaving}
                          >
                            <FontAwesomeIcon icon="trash-alt" className="mr-2" />
                            O'chirish
                          </Button>
                        </div>
                      )}
                      {partnerDetail.blanks.length === 0 ? (
                        <div className="text-center py-4 text-500">Blanklar topilmadi.</div>
                      ) : (
                        <div className="table-responsive">
                          <table className="table table-sm table-hover fs--1 mb-0">
                            <thead className="bg-200 text-900">
                              <tr>
                                {canManagePartners && <th></th>}
                                <th>Barcode</th>
                                <th>Bemor</th>
                                <th>Sana</th>
                                <th>Summa</th>
                                <th>Amallar</th>
                              </tr>
                            </thead>
                            <tbody>
                              {partnerDetail.blanks.map(blank => (
                                <tr key={blank.id}>
                                  {canManagePartners && (
                                    <td className="align-middle">
                                      <Input
                                        type="checkbox"
                                        checked={selectedBlankIds.includes(blank.id)}
                                        onChange={() => handleToggleBlankSelection(blank.id)}
                                      />
                                    </td>
                                  )}
                                  <td className="align-middle font-weight-semi-bold">{blank.barcod}</td>
                                  <td className="align-middle">{blank.client_name || '-'}</td>
                                  <td className="align-middle">{blank.client_date || blank.assigned_at || '-'}</td>
                                  <td className="align-middle">{formatMoney(blank.client_price)}</td>
                                  <td className="align-middle">
                                    <div className="med-actions-row">
                                      {Number(blank.client_id) > 0 && (
                                        <>
                                          <Button
                                            type="button"
                                            color="link"
                                            className="p-0 med-action-btn"
                                            onClick={() => openPatientHistory(blank.client_id)}
                                          >
                                            <FontAwesomeIcon icon="file-alt" />
                                          </Button>
                                          <Button
                                            type="button"
                                            color="link"
                                            className="p-0 med-action-btn"
                                            onClick={() =>
                                              openAnalysisDocumentation({
                                                id: blank.client_id,
                                                full_name: blank.client_name || `Bemor #${blank.client_id}`,
                                                debt: 0
                                              })
                                            }
                                          >
                                            <FontAwesomeIcon icon="flask" />
                                          </Button>
                                        </>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>
                <Col xl={5} className="mb-4">
                  <Card className="border-0 shadow-sm med-dashboard-card h-100">
                    <CardBody className="med-dashboard-card-body">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0">Blanksiz yo'naltirishlar</h5>
                        <Badge color="soft-warning" pill>
                          {partnerDetail.blankless_referrals.length}
                        </Badge>
                      </div>
                      {partnerDetail.blankless_referrals.length === 0 ? (
                        <div className="text-center py-4 text-500">Blanksiz yo'naltirish topilmadi.</div>
                      ) : (
                        <div className="table-responsive">
                          <table className="table table-sm table-hover fs--1 mb-0">
                            <thead className="bg-200 text-900">
                              <tr>
                                <th>Bemor</th>
                                <th>Sana</th>
                                <th>Summa</th>
                                <th>Amallar</th>
                              </tr>
                            </thead>
                            <tbody>
                              {partnerDetail.blankless_referrals.map((referral, index) => (
                                <tr key={`${referral.client_id}-${referral.client_date}-${index}`}>
                                  <td className="align-middle">{referral.client_name || referral.client_id || '-'}</td>
                                  <td className="align-middle">{referral.client_date || '-'}</td>
                                  <td className="align-middle">{formatMoney(referral.client_price)}</td>
                                  <td className="align-middle">
                                    <div className="med-actions-row">
                                      {Number(referral.client_id) > 0 && (
                                        <>
                                          <Button
                                            type="button"
                                            color="link"
                                            className="p-0 med-action-btn"
                                            onClick={() => openPatientHistory(referral.client_id)}
                                          >
                                            <FontAwesomeIcon icon="file-alt" />
                                          </Button>
                                          <Button
                                            type="button"
                                            color="link"
                                            className="p-0 med-action-btn"
                                            onClick={() =>
                                              openAnalysisDocumentation({
                                                id: referral.client_id,
                                                full_name: referral.client_name || `Bemor #${referral.client_id}`,
                                                debt: 0
                                              })
                                            }
                                          >
                                            <FontAwesomeIcon icon="flask" />
                                          </Button>
                                        </>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button type="button" color="light" onClick={() => setPartnerDetailModalOpen(false)}>
            Yopish
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={blankModalOpen} toggle={closeBlankModal} centered>
        <ModalHeader toggle={closeBlankModal}>
          {partnerDetail.partner ? `${partnerDetail.partner.full_name} uchun blank qo'shish` : "Blank qo'shish"}
        </ModalHeader>
        <form onSubmit={handleBlankSubmit}>
          <ModalBody>
            <div className="med-partner-blank-mode mb-3">
              <button
                type="button"
                className={blankEntryMode === 'single' ? 'is-active' : ''}
                onClick={() => {
                  setBlankEntryMode('single');
                  setBlankForm(current => ({ ...current, firstbarcod: '', lastbarcod: '' }));
                }}
              >
                Bitta blank
              </button>
              <button
                type="button"
                className={blankEntryMode === 'range' ? 'is-active' : ''}
                onClick={() => {
                  setBlankEntryMode('range');
                  setBlankForm(current => ({ ...current, onebarcod: '' }));
                }}
              >
                Blank oralig'i
              </button>
            </div>

            <div className="med-partner-blank-entry-card mb-3">
              <div className="d-flex flex-wrap justify-content-between align-items-start mb-3">
                <div>
                  <label className="med-filter-label mb-1 d-block">
                    {blankEntryMode === 'single' ? 'Bitta blank kodi' : 'Blank oralig\'i'}
                  </label>
                  <small className="text-500">
                    {blankEntryMode === 'single'
                      ? 'Bitta aniq barcode kiriting.'
                      : 'Boshlanish va tugash qiymatlarini kiriting, tizim oralig\'ni o\'zi yaratadi.'}
                  </small>
                </div>
                {recommendedNextBarcode && (
                  <div className="med-partner-blank-entry-meta">
                    Tavsiya etilgan keyingi kod: {recommendedNextBarcode}
                  </div>
                )}
              </div>

              {blankEntryMode === 'single' ? (
                <div className="position-relative">
                  <Input
                    value={blankForm.onebarcod}
                    onFocus={() => setBlankSuggestionField('onebarcod')}
                    onChange={event => {
                      const value = event.target.value;
                      setBlankSuggestionField('onebarcod');
                      setBlankForm(current => ({
                        ...current,
                        onebarcod: value,
                        firstbarcod: '',
                        lastbarcod: ''
                      }));
                    }}
                    className="bg-200 border-0"
                    placeholder={recommendedNextBarcode || 'Masalan: 0000001234'}
                  />
                  {blankSuggestionField === 'onebarcod' && (blankSuggestionsLoading || blankSuggestions.length > 0) && (
                    <div className="med-blank-suggestion-list">
                      {blankSuggestionsLoading ? (
                        <div className="med-blank-suggestion-empty">Qidirilmoqda...</div>
                      ) : (
                        blankSuggestions.map(item => (
                          <button key={`${item.id}-${item.barcod}`} type="button" onClick={() => applyBlankSuggestion(item)}>
                            <strong>{item.barcod}</strong>
                            <span>
                              {item.partner_name || 'Biriktirilmagan'} {item.is_used ? '| ishlatilgan' : '| bo‘sh'}
                            </span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <Row>
                  <Col md={6} className="mb-3 mb-md-0">
                    <label className="med-filter-label">Birinchi blank</label>
                    <div className="position-relative">
                      <Input
                        value={blankForm.firstbarcod}
                        onFocus={() => setBlankSuggestionField('firstbarcod')}
                        onChange={event => {
                          const value = event.target.value;
                          setBlankSuggestionField('firstbarcod');
                          setBlankForm(current => ({
                            ...current,
                            firstbarcod: value,
                            onebarcod: ''
                          }));
                        }}
                        className="bg-200 border-0"
                        placeholder={recommendedNextBarcode || 'Boshlanish'}
                      />
                      {blankSuggestionField === 'firstbarcod' && (blankSuggestionsLoading || blankSuggestions.length > 0) && (
                        <div className="med-blank-suggestion-list">
                          {blankSuggestionsLoading ? (
                            <div className="med-blank-suggestion-empty">Qidirilmoqda...</div>
                          ) : (
                            blankSuggestions.map(item => (
                              <button key={`${item.id}-${item.barcod}`} type="button" onClick={() => applyBlankSuggestion(item)}>
                                <strong>{item.barcod}</strong>
                                <span>
                                  {item.partner_name || 'Biriktirilmagan'} {item.is_used ? '| ishlatilgan' : '| bo‘sh'}
                                </span>
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col md={6}>
                    <label className="med-filter-label">Oxirgi blank</label>
                    <div className="position-relative">
                      <Input
                        value={blankForm.lastbarcod}
                        onFocus={() => setBlankSuggestionField('lastbarcod')}
                        onChange={event => {
                          const value = event.target.value;
                          setBlankSuggestionField('lastbarcod');
                          setBlankForm(current => ({
                            ...current,
                            lastbarcod: value,
                            onebarcod: ''
                          }));
                        }}
                        className="bg-200 border-0"
                        placeholder="Tugash"
                      />
                      {blankSuggestionField === 'lastbarcod' && (blankSuggestionsLoading || blankSuggestions.length > 0) && (
                        <div className="med-blank-suggestion-list">
                          {blankSuggestionsLoading ? (
                            <div className="med-blank-suggestion-empty">Qidirilmoqda...</div>
                          ) : (
                            blankSuggestions.map(item => (
                              <button key={`${item.id}-${item.barcod}`} type="button" onClick={() => applyBlankSuggestion(item)}>
                                <strong>{item.barcod}</strong>
                                <span>
                                  {item.partner_name || 'Biriktirilmagan'} {item.is_used ? '| ishlatilgan' : '| bo‘sh'}
                                </span>
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>
              )}
            </div>

            {blankEntryMode === 'range' &&
              blankForm.firstbarcod &&
              blankForm.lastbarcod &&
              Number(blankForm.lastbarcod) >= Number(blankForm.firstbarcod) && (
                <div className="med-partner-blank-entry-meta">
                  Qo'shiladigan blanklar soni: {Number(blankForm.lastbarcod) - Number(blankForm.firstbarcod) + 1}
                </div>
              )}
          </ModalBody>
          <ModalFooter>
            <Button type="button" color="light" onClick={closeBlankModal} disabled={isBlankSaving}>
              Bekor qilish
            </Button>
            <Button type="submit" color="primary" disabled={isBlankSaving}>
              <FontAwesomeIcon icon="save" className="mr-2" />
              {isBlankSaving ? "Saqlanmoqda..." : "Blanklarni saqlash"}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      <Modal isOpen={patientHistoryModalOpen} toggle={() => setPatientHistoryModalOpen(false)} size="xl" centered className="med-doctor-modal">
        <ModalHeader toggle={() => setPatientHistoryModalOpen(false)}>
          {patientHistory.patient ? `${patientHistory.patient.full_name} tarixi` : 'Bemor tarixi'}
        </ModalHeader>
        <ModalBody className="med-doctor-modal-body">
          {isPatientHistoryLoading ? (
            <div className="text-center py-5 text-500">Bemor tarixi yuklanmoqda...</div>
          ) : !patientHistory.patient ? (
            <div className="text-center py-5 text-500">Bemor ma'lumotlari topilmadi.</div>
          ) : (
            <>
              <Row className="mb-4">
                <Col md={6} xl={3} className="mb-3">
                  <div className="med-doctor-summary-card">
                    <small>ID / Filial</small>
                    <strong>{patientHistory.patient.id}</strong>
                    <span>{patientHistory.patient.filial_name || '-'}</span>
                  </div>
                </Col>
                <Col md={6} xl={3} className="mb-3">
                  <div className="med-doctor-summary-card">
                    <small>Tug'ilgan sana</small>
                    <strong>{patientHistory.patient.date_birth || '-'}</strong>
                    <span>{patientHistory.patient.sex_label || '-'}</span>
                  </div>
                </Col>
                <Col md={6} xl={3} className="mb-3">
                  <div className="med-doctor-summary-card">
                    <small>Telefon</small>
                    <strong>{patientHistory.patient.phone || '-'}</strong>
                    <span>{patientHistory.patient.email || '-'}</span>
                  </div>
                </Col>
                <Col md={6} xl={3} className="mb-3">
                  <div className="med-doctor-summary-card">
                    <small>Qarz / To'lovlar</small>
                    <strong>{formatMoney(patientHistory.patient.debt)}</strong>
                    <span>{patientHistory.summary?.payment_count || 0} ta to'lov</span>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col xl={7} className="mb-4">
                  <Card className="border-0 shadow-sm med-dashboard-card h-100">
                    <CardBody className="med-dashboard-card-body">
                      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                        <h5 className="mb-0">Xizmatlar va loglar</h5>
                        <div className="d-flex align-items-center mt-2 mt-md-0">
                          <Badge color="soft-primary" pill className="mr-2">
                            {patientHistory.summary?.log_count || 0}
                          </Badge>
                          <Button
                            type="button"
                            color="outline-primary"
                            size="sm"
                            className="med-outline-add-btn"
                            onClick={() => openAnalysisDocumentation(patientHistory.patient)}
                          >
                            <FontAwesomeIcon icon="flask" className="mr-2" />
                            Analiz natijasi
                          </Button>
                        </div>
                      </div>
                      {patientHistory.logs.length === 0 ? (
                        <div className="text-center py-4 text-500">Yozuvlar topilmadi.</div>
                      ) : (
                        <div className="table-responsive">
                          <table className="table table-sm table-hover fs--1 mb-0">
                            <thead className="bg-200 text-900">
                              <tr>
                                <th>Xizmat</th>
                                <th>Tur</th>
                                <th>Sana</th>
                                <th>Summa</th>
                                <th>Hamkor</th>
                              </tr>
                            </thead>
                            <tbody>
                              {patientHistory.logs.map(item => (
                                <tr key={item.id}>
                                  <td className="align-middle">
                                    <div className="font-weight-semi-bold">{item.title}</div>
                                    <small className="text-500">
                                      {item.barcod ? `Blank: ${item.barcod}` : 'Blanksiz'} {item.result ? `| Natija: ${item.result}` : ''}
                                    </small>
                                  </td>
                                  <td className="align-middle text-capitalize">{item.type || '-'}</td>
                                  <td className="align-middle">{item.date || '-'}</td>
                                  <td className="align-middle">{formatMoney(item.price)}</td>
                                  <td className="align-middle">{item.partner_name || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>
                <Col xl={5} className="mb-4">
                  <Card className="border-0 shadow-sm med-dashboard-card h-100">
                    <CardBody className="med-dashboard-card-body">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0">To'lovlar tarixi</h5>
                        <Badge color="soft-success" pill>
                          {formatMoney(patientHistory.summary?.payment_amount || 0)}
                        </Badge>
                      </div>
                      {patientHistory.payments.length === 0 ? (
                        <div className="text-center py-4 text-500">To'lovlar topilmadi.</div>
                      ) : (
                        <div className="table-responsive">
                          <table className="table table-sm table-hover fs--1 mb-0">
                            <thead className="bg-200 text-900">
                              <tr>
                                <th>Sana</th>
                                <th>Tur</th>
                                <th>Summa</th>
                                <th>Kassir</th>
                              </tr>
                            </thead>
                            <tbody>
                              {patientHistory.payments.map(payment => (
                                <tr key={payment.id}>
                                  <td className="align-middle">{payment.date || '-'}</td>
                                  <td className="align-middle">
                                    <div>{payment.payment_method || '-'}</div>
                                    <small className="text-500">{payment.discount_label || payment.description || '-'}</small>
                                  </td>
                                  <td className="align-middle">{formatMoney(payment.amount)}</td>
                                  <td className="align-middle">{payment.cashier_name || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button type="button" color="light" onClick={() => setPatientHistoryModalOpen(false)}>
            Yopish
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={analysisModalOpen} toggle={closeAnalysisModal} size="lg" centered className="med-confirm-modal">
        <ModalHeader toggle={closeAnalysisModal}>
          {analysisPatient ? `${analysisPatient.full_name} bo'yicha analizlar` : 'Analizlar'}
        </ModalHeader>
        <ModalBody className="med-analysis-doc-modal-body">
          {analysisPatient && (
            <div className="med-analysis-doc-header">
              <div className="med-analysis-doc-header-copy">
                <strong>{analysisPatient.full_name}</strong>
                <span>ID: {analysisPatient.id}</span>
              </div>
              <Badge color="soft-primary" pill>
                Laboratoriya
              </Badge>
            </div>
          )}

          {analysisLoading ? (
            <div className="text-center py-5 text-500">Analizlar yuklanmoqda...</div>
          ) : analysisItems.length === 0 ? (
            <div className="med-dashboard-empty mt-3">Bu bemor uchun laboratoriya yozuvlari topilmadi.</div>
          ) : (
            <div className="med-analysis-doc-list">
              {analysisItems.map(item => {
                const draft = analysisDrafts[item.id] || {
                  result: item.result || '',
                  status: item.status_key || 'pending',
                  template_id: item.template_id ? String(item.template_id) : '',
                  method_id: item.method_id ? String(item.method_id) : '',
                  norm_ids: item.norm_ids || []
                };
                const selectedMethodId = Number(draft.method_id || 0);
                const visibleNorms = (item.norm_options || []).filter(norm => !selectedMethodId || Number(norm.method_id) === selectedMethodId);

                return (
                  <div key={item.id} className="med-analysis-doc-item">
                    <div className="med-analysis-doc-top">
                      <div>
                        <h6>{item.analysis_title || 'Analiz'}</h6>
                        <p>
                          {item.method_title || "Bo'lim ko'rsatilmagan"} | {item.date || '-'}
                        </p>
                      </div>
                      <Badge
                        color={
                          draft.status === 'printed'
                            ? 'soft-success'
                            : draft.status === 'ready'
                              ? 'soft-primary'
                              : draft.status === 'in_progress'
                                ? 'soft-warning'
                                : 'soft-secondary'
                        }
                        pill
                      >
                        {draft.status === 'printed'
                          ? 'Chop etilgan'
                          : draft.status === 'ready'
                            ? 'Tayyor'
                            : draft.status === 'in_progress'
                              ? 'Jarayonda'
                              : 'Kutilmoqda'}
                      </Badge>
                    </div>
                    <div className="med-analysis-doc-meta">
                      <span>Qisqa nom: {item.analysis_short || '-'}</span>
                      <span>Saqlangan vaqt: {item.saved_at || item.date || '-'}</span>
                    </div>

                    {Number(item.result_type_value) !== 1 ? (
                      <>
                        <div className="mt-3">
                          <label className="med-filter-label">Shablon</label>
                          <Input
                            type="select"
                            value={draft.template_id}
                            onChange={event => handleAnalysisTemplateChange(item, event.target.value)}
                            className="bg-200 border-0"
                          >
                            <option value="">Shablon tanlang</option>
                            {(item.templates || []).map(template => (
                              <option key={template.id} value={template.id}>
                                {template.name}
                              </option>
                            ))}
                          </Input>
                        </div>
                        <div className="mt-3">
                          <label className="med-filter-label">Natija matni</label>
                          <QuillEditor
                            value={draft.result || ''}
                            onChange={value => updateAnalysisDraft(item.id, { result: value })}
                            className="med-analysis-doc-editor"
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <Row className="mt-3">
                          <Col md={4} className="mb-3">
                            <label className="med-filter-label">Holat</label>
                            <Input
                              type="select"
                              value={draft.status}
                              onChange={event => updateAnalysisDraft(item.id, { status: event.target.value })}
                              className="bg-200 border-0"
                            >
                              <option value="pending">Kutilmoqda</option>
                              <option value="in_progress">Jarayonda</option>
                              <option value="ready">Tayyor</option>
                              <option value="printed">Chop etilgan</option>
                            </Input>
                          </Col>
                          <Col md={4} className="mb-3">
                            <label className="med-filter-label">Metod</label>
                            <Input
                              type="select"
                              value={draft.method_id}
                              onChange={event => handleAnalysisMethodChange(item.id, event.target.value)}
                              className="bg-200 border-0"
                            >
                              <option value="">Tanlang</option>
                              {(item.methods || []).map(method => (
                                <option key={method.id} value={method.id}>
                                  {method.title}
                                </option>
                              ))}
                            </Input>
                          </Col>
                          <Col md={4} className="mb-3">
                            <label className="med-filter-label">Natija</label>
                            {item.has_negative_option ? (
                              <Input
                                type="select"
                                value={draft.result}
                                onChange={event => updateAnalysisDraft(item.id, { result: event.target.value })}
                                className="bg-200 border-0"
                              >
                                <option value="">Tanlang</option>
                                <option value="negative">negative</option>
                                <option value="positive">positive</option>
                                <option value="grey_zone">grey_zone</option>
                              </Input>
                            ) : (
                              <Input
                                value={draft.result}
                                onChange={event => updateAnalysisDraft(item.id, { result: event.target.value })}
                                className="bg-200 border-0"
                                placeholder="Natijani kiriting"
                              />
                            )}
                          </Col>
                        </Row>
                        {visibleNorms.length > 0 && (
                          <div className="mt-2">
                            <label className="med-filter-label d-block">Normlar</label>
                            <div className="med-analysis-doc-norms">
                              {visibleNorms.map(norm => (
                                <label
                                  key={`${item.id}-${norm.id}`}
                                  className={`med-analysis-doc-norm${draft.norm_ids.includes(Number(norm.id)) ? ' is-active' : ''}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={draft.norm_ids.includes(Number(norm.id))}
                                    onChange={() => toggleAnalysisNorm(item.id, norm.id)}
                                  />
                                  <span>{norm.norm}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {Number(item.result_type_value) !== 1 && (
                      <div className="mt-3">
                        <label className="med-filter-label">Holat</label>
                        <Input
                          type="select"
                          value={draft.status}
                          onChange={event => updateAnalysisDraft(item.id, { status: event.target.value })}
                          className="bg-200 border-0"
                        >
                          <option value="pending">Kutilmoqda</option>
                          <option value="in_progress">Jarayonda</option>
                          <option value="ready">Tayyor</option>
                          <option value="printed">Chop etilgan</option>
                        </Input>
                      </div>
                    )}

                    <div className="med-analysis-doc-result">{analysisPreview(draft.result)}</div>
                    <div className="text-right mt-3">
                      <Button color="primary" size="sm" onClick={() => saveAnalysisItem(item)} disabled={analysisSavingId === item.id}>
                        <FontAwesomeIcon
                          icon={analysisSavingId === item.id ? 'spinner' : 'check-circle'}
                          spin={analysisSavingId === item.id}
                          className="mr-2"
                        />
                        Saqlash
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={closeAnalysisModal}>
            Yopish
          </Button>
        </ModalFooter>
      </Modal>

      <ActionConfirmModal
        isOpen={Boolean(deleteTarget)}
        toggle={() => setDeleteTarget(null)}
        title="Hamkor shifokorni o'chirish"
        message={deleteTarget ? `${deleteTarget.name} yozuvini o'chirmoqchimisiz?` : ''}
        confirmLabel="O'chirish"
        onConfirm={handleDeletePartner}
      />
    </>
  );
};

export default Doctors;
